import {
    Box,
    Button,
    Checkbox,
    Divider,
    Group,
    Modal,
    Stack,
    Text,
    Title,
    useMantineTheme,
} from "@mantine/core";
import { IconAlertCircle } from "@tabler/icons-react";
import { useEffect, useState } from "react";

interface TermsOfUseModalProps {
    opened: boolean;
    onClose: () => void;
}

export function TermsOfUseModal({ opened, onClose }: TermsOfUseModalProps) {
    const [isConfirmed, setIsConfirmed] = useState(false);
    const [isReady, setIsReady] = useState(false);
    const theme = useMantineTheme();

    useEffect(() => {
        if (opened) {
            const storedValue = localStorage.getItem("termsOfUse_accepted");
            if (storedValue) {
                setIsConfirmed(true);
            }

            setTimeout(() => {
                setIsReady(true);
            }, 50);
        } else {
            setIsReady(false);
        }
    }, [opened]);

    const handleAccept = () => {
        localStorage.setItem("termsOfUse_accepted", "true");
        onClose();
    };

    return (
        <Modal
            opened={opened && isReady}
            onClose={() => {}}
            title={
                <Group gap="xs">
                    <IconAlertCircle size={20} color={theme.colors.purple[7]} />
                    <Title order={3} c="purple.8">
                        Terms of Use
                    </Title>
                </Group>
            }
            size="lg"
            centered
            closeOnClickOutside={false}
            closeOnEscape={false}
            withCloseButton={false}
            transitionProps={{ duration: 300, transition: "fade" }}
            style={(theme) => ({
                title: {
                    fontWeight: 600,
                    fontSize: theme.fontSizes.lg,
                },
                header: {
                    paddingBottom: 0,
                },
                body: {
                    paddingTop: theme.spacing.xs,
                },
                overlay: {
                    opacity: 0.4,
                },
                modal: {
                    boxShadow: "0 8px 16px rgba(0, 0, 0, 0.2)",
                },
            })}
        >
            <Divider mb="md" />

            <Box p="sm">
                <Text mb="md">
                    It is the users responsibility to ensure that the information acquired through
                    the Knowledge Search, including access to further technical details, is not
                    exported without complying with any applicable Export Controls regulations, and
                    that any such information is not utilized to assist any government's military
                    operations, military organizations, or for any military end-use.
                </Text>

                <Text fw={600}>
                    By using this application, you confirm your compliance with the statement
                    provided above.
                </Text>
            </Box>

            <Divider my="md" />

            <Stack gap="md" px="md" pb="md">
                <Checkbox
                    checked={isConfirmed}
                    onChange={(e) => setIsConfirmed(e.currentTarget.checked)}
                    label={<Text fw={500}>I confirm my compliance with the Terms of Use</Text>}
                    size="md"
                    color="purple"
                />

                <Group justify="flex-end" mt="md">
                    <Button
                        data-testid="confirm-terms-button"
                        disabled={!isConfirmed}
                        onClick={handleAccept}
                        color="purple.8"
                        size="md"
                    >
                        Next
                    </Button>
                </Group>
            </Stack>
        </Modal>
    );
}

export function useTermsOfUse() {
    const [modalOpened, setModalOpened] = useState(false);
    const [isMounted, setIsMounted] = useState(false);

    useEffect(() => {
        setIsMounted(true);
        const termsAccepted = localStorage.getItem("termsOfUse_accepted");
        const _lastLoginTime = localStorage.getItem("lastLoginTime");

        if (!termsAccepted) {
            setTimeout(() => {
                setModalOpened(true);
            }, 100);
        }

        return () => {
            setIsMounted(false);
        };
    }, []);

    return {
        termsModalOpened: isMounted && modalOpened,
        closeTermsModal: () => setModalOpened(false),
        showTermsModal: () => setModalOpened(true),
    };
}
